import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Send,
  Loader2,
  MessageSquare,
} from 'lucide-react';
import type { ReaderDataProvider, ReaderUser, Discussion, ReaderThemeMode } from '../types';

interface ChapterDiscussionProps {
  chapterId: string;
  theme: ReaderThemeMode;
  dataProvider: ReaderDataProvider;
  user: ReaderUser | null;
}

const discussionPrompts = [
  'What stood out to you most in this chapter?',
  'How are you planning to apply these concepts?',
  'Any questions or challenges you want to discuss?',
];

export default function ChapterDiscussion({ chapterId, theme, dataProvider, user }: ChapterDiscussionProps) {
  const [newComment, setNewComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [discussions, setDiscussions] = useState<Discussion[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const isDark = theme === 'dark';
  const isSepia = theme === 'sepia';

  const textColor = isDark ? '#e4e4e7' : isSepia ? '#5c4b37' : 'var(--text)';
  const secondaryColor = isDark ? 'rgba(255,255,255,0.6)' : isSepia ? '#8b7355' : 'var(--text-light)';
  const borderColor = isDark ? 'rgba(255,255,255,0.1)' : isSepia ? '#d4c4a8' : 'var(--light-gray)';
  const cardBg = isDark ? 'rgba(255,255,255,0.03)' : isSepia ? '#ebe3d0' : 'var(--off-white)';

  // Fetch discussions
  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);

    dataProvider.getChapterDiscussions(chapterId).then((data) => {
      if (!cancelled) {
        setDiscussions(data);
        setIsLoading(false);
      }
    }).catch(() => {
      if (!cancelled) {
        setDiscussions([]);
        setIsLoading(false);
      }
    });

    return () => { cancelled = true; };
  }, [chapterId, dataProvider]);

  const submitComment = async () => {
    if (!newComment.trim() || !user) return;

    setIsSubmitting(true);
    try {
      await dataProvider.postDiscussion(chapterId, newComment.trim());
      setNewComment('');
      // Refresh discussions
      const updated = await dataProvider.getChapterDiscussions(chapterId);
      setDiscussions(updated);
    } catch (error) {
      console.error('Failed to post comment:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatTimestamp = (timestamp: number) => {
    const now = Date.now();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return new Date(timestamp).toLocaleDateString();
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Loading state
  if (isLoading) {
    return (
      <div
        style={{
          marginTop: '1.5rem',
          padding: '2rem',
          background: cardBg,
          borderRadius: '0 0 0.75rem 0.75rem',
          textAlign: 'center',
        }}
      >
        <Loader2 size={24} style={{ animation: 'spin 1s linear infinite', color: secondaryColor }} />
        <p style={{ color: secondaryColor, marginTop: '0.5rem', fontSize: '0.875rem' }}>
          Loading discussions...
        </p>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div
      style={{
        marginTop: '1.5rem',
        padding: '1.5rem',
        background: cardBg,
        borderRadius: '0 0 0.75rem 0.75rem',
        borderTop: 'none',
      }}
    >
      {/* Discord Sync Badge */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          marginBottom: '1rem',
          padding: '0.5rem 0.75rem',
          background: isDark ? 'rgba(88, 101, 242, 0.1)' : '#eef2ff',
          borderRadius: '6px',
          width: 'fit-content',
        }}
      >
        <svg width="16" height="12" viewBox="0 0 71 55" fill="none">
          <path
            d="M60.1045 4.8978C55.5792 2.8214 50.7265 1.2916 45.6527 0.41542C45.5603 0.39851 45.468 0.440769 45.4204 0.525289C44.7963 1.6353 44.105 3.0834 43.6209 4.2216C38.1637 3.4046 32.7345 3.4046 27.3892 4.2216C26.905 3.0581 26.1886 1.6353 25.5617 0.525289C25.5141 0.443589 25.4218 0.40133 25.3294 0.41542C20.2584 1.2888 15.4057 2.8186 10.8776 4.8978C10.8384 4.9147 10.8048 4.9429 10.7825 4.9795C1.57795 18.7309 -0.943561 32.1443 0.293408 45.3914C0.299005 45.4562 0.335386 45.5182 0.385761 45.5576C6.45866 50.0174 12.3413 52.7249 18.1147 54.5195C18.2071 54.5477 18.305 54.5139 18.3638 54.4378C19.7295 52.5728 20.9469 50.6063 21.9907 48.5383C22.0523 48.4172 21.9935 48.2735 21.8676 48.2256C19.9366 47.4931 18.0979 46.6 16.3292 45.5858C16.1893 45.5041 16.1781 45.304 16.3068 45.2082C16.679 44.9293 17.0513 44.6391 17.4067 44.3461C17.471 44.2926 17.5606 44.2813 17.6362 44.3151C29.2558 49.6202 41.8354 49.6202 53.3179 44.3151C53.3935 44.2785 53.4831 44.2898 53.5502 44.3433C53.9057 44.6363 54.2779 44.9293 54.6529 45.2082C54.7816 45.304 54.7732 45.5041 54.6333 45.5858C52.8646 46.6197 51.0259 47.4931 49.0921 48.2228C48.9662 48.2707 48.9102 48.4172 48.9718 48.5383C50.0385 50.6034 51.256 52.5699 52.5765 54.435C52.6324 54.5139 52.7331 54.5477 52.8255 54.5195C58.6241 52.7249 64.5068 50.0174 70.5797 45.5576C70.6329 45.5182 70.6665 45.459 70.6749 45.3942C72.1697 29.9841 68.1714 16.7119 60.1968 4.9823C60.1772 4.9429 60.1437 4.9147 60.1045 4.8978Z"
            fill="#5865F2"
          />
        </svg>
        <span style={{ fontSize: '0.75rem', color: '#5865F2', fontWeight: 500 }}>
          Synced with Discord
        </span>
      </div>

      {/* Prompts */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '0.5rem',
          marginBottom: '1.5rem',
        }}
      >
        {discussionPrompts.map((prompt, index) => (
          <button
            key={index}
            onClick={() => setNewComment(prompt + ' ')}
            style={{
              padding: '0.5rem 1rem',
              background: isDark ? 'rgba(255,255,255,0.05)' : 'white',
              border: `1px solid ${borderColor}`,
              borderRadius: '9999px',
              fontSize: '0.8125rem',
              color: secondaryColor,
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
          >
            {prompt}
          </button>
        ))}
      </div>

      {/* New Comment Input */}
      {user ? (
        <div
          style={{
            display: 'flex',
            gap: '1rem',
            marginBottom: '2rem',
          }}
        >
          <div
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              background: 'var(--accent)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontWeight: 600,
              fontSize: '0.875rem',
              flexShrink: 0,
            }}
          >
            {getInitials(user.name || user.email || 'U')}
          </div>
          <div style={{ flex: 1 }}>
            <textarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Share your thoughts on this chapter..."
              style={{
                width: '100%',
                padding: '0.875rem 1rem',
                borderRadius: '0.75rem',
                border: `1px solid ${borderColor}`,
                background: isDark ? 'rgba(255,255,255,0.05)' : 'white',
                color: textColor,
                fontSize: '0.9375rem',
                resize: 'none',
                minHeight: '80px',
                fontFamily: 'inherit',
              }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.75rem' }}>
              <button
                onClick={submitComment}
                disabled={!newComment.trim() || isSubmitting}
                style={{
                  padding: '0.625rem 1.25rem',
                  background: newComment.trim() && !isSubmitting ? 'var(--accent)' : borderColor,
                  color: newComment.trim() && !isSubmitting ? 'white' : secondaryColor,
                  border: 'none',
                  borderRadius: '0.5rem',
                  cursor: newComment.trim() && !isSubmitting ? 'pointer' : 'not-allowed',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  transition: 'all 0.2s',
                }}
              >
                {isSubmitting ? (
                  <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                ) : (
                  <Send size={16} />
                )}
                {isSubmitting ? 'Posting...' : 'Post Comment'}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div
          style={{
            padding: '1.5rem',
            background: isDark ? 'rgba(255,255,255,0.05)' : 'white',
            borderRadius: '0.75rem',
            textAlign: 'center',
            marginBottom: '2rem',
            border: `1px solid ${borderColor}`,
          }}
        >
          <p style={{ color: secondaryColor, margin: 0 }}>
            Sign in to join the discussion
          </p>
        </div>
      )}

      {/* Comments List */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
        {!discussions || discussions.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <MessageSquare size={32} color={secondaryColor} style={{ marginBottom: '0.75rem' }} />
            <p style={{ color: secondaryColor, margin: 0 }}>
              No comments yet. Be the first to share your thoughts!
            </p>
          </div>
        ) : (
          discussions.map((comment: Discussion) => (
            <motion.div
              key={comment.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div style={{ display: 'flex', gap: '1rem' }}>
                {/* Avatar */}
                <div
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    background:
                      comment.source === 'discord'
                        ? 'linear-gradient(135deg, #5865F2 0%, #4752C4 100%)'
                        : 'var(--primary)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    flexShrink: 0,
                    overflow: 'hidden',
                  }}
                >
                  {comment.userAvatar ? (
                    <img
                      src={comment.userAvatar}
                      alt={comment.userName}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    getInitials(comment.userName)
                  )}
                </div>

                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      marginBottom: '0.375rem',
                      flexWrap: 'wrap',
                    }}
                  >
                    <span style={{ fontWeight: 600, color: textColor, fontSize: '0.9375rem' }}>
                      {comment.userName}
                    </span>
                    {comment.source === 'discord' && (
                      <span
                        style={{
                          padding: '0.125rem 0.5rem',
                          background: isDark ? 'rgba(88, 101, 242, 0.2)' : '#eef2ff',
                          color: '#5865F2',
                          borderRadius: '9999px',
                          fontSize: '0.6875rem',
                          fontWeight: 600,
                        }}
                      >
                        Discord
                      </span>
                    )}
                    <span style={{ fontSize: '0.8125rem', color: secondaryColor }}>
                      {formatTimestamp(comment.createdAt)}
                    </span>
                  </div>

                  <p
                    style={{
                      color: textColor,
                      lineHeight: 1.6,
                      margin: 0,
                      fontSize: '0.9375rem',
                    }}
                  >
                    {comment.content}
                  </p>
                </div>
              </div>
            </motion.div>
          ))
        )}
      </div>
    </div>
  );
}
